/**
 * 组织架构管理页面
 * 左侧组织树 + 右侧详情/成员
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Tree, Input, Tabs, Descriptions, Tag, Button,
  Space, Statistic, Modal, message, Empty, Spin, Tooltip, Switch, Table,
  Upload, Popconfirm, Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ApartmentOutlined, TeamOutlined, BankOutlined, ClusterOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined,
  StopOutlined, DragOutlined, ReloadOutlined,
  ExpandAltOutlined, ShrinkOutlined,
  CopyOutlined, UploadOutlined, DownloadOutlined,
} from '@ant-design/icons';
import type { TreeProps } from 'antd';
import {
  getOrganizationTree,
  getOrganizationDetail,
  deleteOrganization,
  getOrganizationStatistics,
  activateOrganization,
  deactivateOrganization,
  moveOrganization,
  getOrganizationMemberStatistics,
  copyOrganization,
  importOrganizations,
  exportOrganizations,
  setOrganizationLeader,
  removeOrganizationLeader,
  setOrganizationAssistants,
} from '../../api/organization';
import { getUserList } from '../../api/user';
import type {
  Organization,
  OrganizationTreeNode,
  OrganizationStatistics,
} from '../../api/types';
import Permission from '@/modules/shared/components/common/Permission';
import {
  PERM_ORGS_CREATE, PERM_ORGS_UPDATE, PERM_ORGS_DELETE,
} from '@/constants/permissions';
import { OrganizationType } from '../../api/types';
import OrganizationForm from './OrganizationForm';
import MemberList from './MemberList';
import type { RelationType } from '@/api/organization';

const { confirm } = Modal;

/** 组织类型显示文本 */
const typeLabels: Record<number, string> = {
  [OrganizationType.COMPANY]: '公司',
  [OrganizationType.DEPARTMENT]: '部门',
  [OrganizationType.GROUP]: '分组',
  [OrganizationType.TEAM]: '团队',
};

/** 组织类型标签颜色 */
const typeColors: Record<number, string> = {
  [OrganizationType.COMPANY]: 'purple',
  [OrganizationType.DEPARTMENT]: 'blue',
  [OrganizationType.GROUP]: 'green',
  [OrganizationType.TEAM]: 'orange',
};

const RELATION_TYPE_TO_ORG_TYPE: Partial<Record<RelationType, OrganizationType>> = {
  headquarters: OrganizationType.COMPANY,
  subsidiary: OrganizationType.COMPANY,
  branch: OrganizationType.COMPANY,
  division: OrganizationType.DEPARTMENT,
  region: OrganizationType.GROUP,
  store: OrganizationType.TEAM,
  direct_store: OrganizationType.TEAM,
};

const getRelationTypes = (source?: any): RelationType[] => {
  if (!source) return [];
  return (source.relationTypes ?? source.relation_types ?? []) as RelationType[];
};

const getOrganizationType = (source?: any): OrganizationType => {
  const explicitType = Number(source?.type);
  if (typeLabels[explicitType]) return explicitType as OrganizationType;

  const relationType = getRelationTypes(source)[0];
  return RELATION_TYPE_TO_ORG_TYPE[relationType] ?? OrganizationType.DEPARTMENT;
};

/** 从树数据计算统计（纯函数，模块级，无副作用） */
function computeStatisticsFromTree(nodes: OrganizationTreeNode[]): OrganizationStatistics {
  let total = 0, members = 0, companies = 0, departments = 0;
  const walk = (list: OrganizationTreeNode[]) => {
    for (const n of list) {
      total++;
      members += n.memberCount || 0;
      if (n.type === OrganizationType.COMPANY) companies++;
      if (n.type === OrganizationType.DEPARTMENT) departments++;
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return {
    totalOrganizations: total,
    totalMembers: members,
    companies,
    departments,
    groups: 0,
    teams: 0,
    avgMembersPerOrg: total > 0 ? Math.round(members / total) : 0,
    organizationGrowth: 0,
    memberGrowth: 0,
  };
}

const OrganizationManagement: React.FC = () => {
  // 统计数据
  const [statistics, setStatistics] = useState<OrganizationStatistics | null>(null);

  // 树相关
  const [treeData, setTreeData] = useState<OrganizationTreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filterType, setFilterType] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  // 详情相关
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // 表单相关
  const [formVisible, setFormVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [parentIdForCreate, setParentIdForCreate] = useState<string | undefined>(undefined);

  // 拖拽排序
  const [draggable, setDraggable] = useState(false);

  // 成员统计
  const [memberStats, setMemberStats] = useState<any>(null);
  const [memberStatsLoading, setMemberStatsLoading] = useState(false);

  // 子组织
  const [childOrgs, setChildOrgs] = useState<Organization[]>([]);
  const [childOrgsLoading, setChildOrgsLoading] = useState(false);

  // 导入
  const [importVisible, setImportVisible] = useState(false);

  // 导出
  const [exportLoading, setExportLoading] = useState(false);

  // 复制组织
  const [copyVisible, setCopyVisible] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [copyCode, setCopyCode] = useState('');

  // 负责人管理
  const [leaderVisible, setLeaderVisible] = useState(false);
  const [leaderUserId, setLeaderUserId] = useState<string | undefined>(undefined);

  // 助理管理
  const [assistantsVisible, setAssistantsVisible] = useState(false);
  const [assistantUserIds, setAssistantUserIds] = useState<string[]>([]);

  // 用户搜索（负责人/助理选择用）
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  /** 获取统计数据 */
  const fetchStatistics = useCallback(async () => {
    try {
      const res = await getOrganizationStatistics();
      if (res?.data) {
        setStatistics(res.data as any);
      }
    } catch {
      // 后端暂无此接口, 静默失败, 由 tree 加载后计算
    }
  }, []);

  /** 将后端树节点转换为前端 OrganizationTreeNode 格式 */
  const transformTreeNodes = (nodes: any[]): OrganizationTreeNode[] =>
    nodes.map((n) => ({
      key: String(n.key ?? n.id),
      title: n.title ?? n.name,
      value: String(n.value ?? n.id),
      type: getOrganizationType(n),
      memberCount: n.memberCount ?? n.member_count ?? n.userCount ?? n.user_count ?? 0,
      children: n.children?.length ? transformTreeNodes(n.children) : undefined,
    }));

  /** 获取组织树 */
  const fetchTree = useCallback(async () => {
    setTreeLoading(true);
    try {
      const res = await getOrganizationTree();
      if (res?.data) {
        const data = res.data as any;
        const raw = Array.isArray(data)
          ? data
          : data.data || [];
        const nodes = transformTreeNodes(raw);
        setTreeData(nodes);
        // 默认展开前两层（根节点 + 二级节点）
        const keysToExpand: React.Key[] = [];
        for (const n of nodes) {
          keysToExpand.push(n.key);
          if (n.children?.length) {
            for (const child of n.children) {
              keysToExpand.push(child.key);
            }
          }
        }
        setExpandedKeys(keysToExpand);
        // 默认选中第一个根节点并加载详情
        if (nodes.length > 0) {
          const firstKey = nodes[0].key;
          setSelectedKeys([firstKey]);
          fetchDetail(firstKey);
        }
        // 如果没有统计数据, 从树数据计算（只计算一次，不引入 statistics dep）
        setStatistics(prev => prev ?? computeStatisticsFromTree(nodes));
      }
    } catch (error) {
      console.error('获取组织树失败', error);
    } finally {
      setTreeLoading(false);
    }
  }, []);

  /** 获取组织详情 */
  const fetchDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await getOrganizationDetail(id);
      if (res?.data) {
        const raw = res.data as any;
        // 映射后端字段到前端 Organization 接口
        setSelectedOrg({
          ...raw,
          type: getOrganizationType(raw),
          member_count: raw.member_count ?? raw.user_count ?? 0,
          sort: raw.sort ?? raw.sort_order ?? 0,
          description: raw.description ?? raw.remark ?? '',
          leader_name: raw.leader_name ?? raw.contact_name ?? '',
          leader_id: raw.leader_id ?? undefined,
        });
      }
    } catch (error) {
      console.error('获取组织详情失败', error);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /** 获取成员统计 */
  const fetchMemberStats = useCallback(async (id: string) => {
    setMemberStatsLoading(true);
    try {
      const res: any = await getOrganizationMemberStatistics(id);
      const data = res?.data?.data || res?.data;
      setMemberStats(data);
    } catch {
      setMemberStats(null);
    } finally {
      setMemberStatsLoading(false);
    }
  }, []);

  /** 从树数据中提取子组织 */
  const findChildOrgsFromTree = useCallback((id: string, nodes: OrganizationTreeNode[]): Organization[] => {
    for (const n of nodes) {
      if (n.key === id) {
        return (n.children || []).map((c) => ({
          id: c.key,
          name: c.title,
          code: '',
          type: c.type,
          level: 0,
          sort: 0,
          member_count: c.memberCount,
          status: 'active' as const,
          created_at: '',
          updated_at: '',
        }));
      }
      if (n.children?.length) {
        const found = findChildOrgsFromTree(id, n.children);
        if (found.length > 0) return found;
      }
    }
    return [];
  }, []);

  /** 获取子组织列表 */
  const fetchChildOrgs = useCallback((id: string) => {
    setChildOrgsLoading(true);
    try {
      const items = findChildOrgsFromTree(id, treeData);
      setChildOrgs(items);
    } catch {
      setChildOrgs([]);
    } finally {
      setChildOrgsLoading(false);
    }
  }, [treeData, findChildOrgsFromTree]);

  /** 处理拖拽排序 */
  const handleDrop: TreeProps['onDrop'] = async (info) => {
    const dragNodeId = String(info.dragNode.key);
    const dropNodeId = String(info.node.key);
    const dropToGap = info.dropToGap;

    // Root nodes (top-level) are the headquarters and cannot be moved
    if (treeData.some(n => String(n.key) === dragNodeId)) {
      message.warning('根组织（总部）不可移动');
      return;
    }

    try {
      if (dropToGap) {
        // 拖到节点间隙 → 移动为同级，排在 dropNode 位置
        // 获取 dropNode 的 parentId
        const findParent = (nodes: OrganizationTreeNode[], targetKey: string, parent?: string): string | undefined => {
          for (const n of nodes) {
            if (n.key === targetKey) return parent;
            if (n.children?.length) {
              const found = findParent(n.children, targetKey, n.key);
              if (found !== undefined) return found;
            }
          }
          return undefined;
        };
        const parentId = findParent(treeData, dropNodeId);
        await moveOrganization(dragNodeId, parentId, info.dropPosition);
      } else {
        // 拖到节点上 → 移动为子节点
        await moveOrganization(dragNodeId, dropNodeId);
      }
      message.success('移动成功');
      fetchTree();
      fetchStatistics();
    } catch {
      message.error('移动失败');
    }
  };

  /** 收集所有树节点 key */
  const collectAllKeys = (nodes: OrganizationTreeNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const walk = (list: OrganizationTreeNode[]) => {
      for (const n of list) {
        keys.push(n.key);
        if (n.children?.length) walk(n.children);
      }
    };
    walk(nodes);
    return keys;
  };

  /** 按类型过滤组织树（保留匹配节点及其祖先/后代） */
  const filterTreeByType = (nodes: OrganizationTreeNode[], type: number): OrganizationTreeNode[] =>
    nodes.reduce<OrganizationTreeNode[]>((acc, node) => {
      const filteredChildren = node.children ? filterTreeByType(node.children, type) : [];
      if (node.type === type || filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      }
      return acc;
    }, []);

  /** 当前展示的树数据（叠加类型过滤） */
  const displayTreeData = filterType ? filterTreeByType(treeData, filterType) : treeData;

  // 初始加载
  useEffect(() => {
    fetchTree();
    fetchStatistics();
  }, [fetchTree, fetchStatistics]);

  /** 选中组织树节点 */
  const handleSelect: TreeProps['onSelect'] = (keys) => {
    setSelectedKeys(keys);
    if (keys.length > 0) {
      const id = String(keys[0]);
      fetchDetail(id);
      fetchChildOrgs(id);
      setActiveTab('info');
    } else {
      setSelectedOrg(null);
      setChildOrgs([]);
    }
  };

  /** 展开/折叠 */
  const handleExpand: TreeProps['onExpand'] = (keys) => {
    setExpandedKeys(keys);
    setAutoExpandParent(false);
  };

  /** 搜索组织树 - 收集所有匹配节点的key及其祖先key */
  const getMatchedKeys = (
    nodes: OrganizationTreeNode[],
    value: string,
  ): React.Key[] => {
    const keys: React.Key[] = [];
    const loop = (data: OrganizationTreeNode[], parentKeys: React.Key[]): boolean => {
      let hasMatch = false;
      for (const node of data) {
        const currentPath = [...parentKeys, node.key];
        const titleMatch = node.title.toLowerCase().includes(value.toLowerCase());
        let childMatch = false;

        if (node.children && node.children.length > 0) {
          childMatch = loop(node.children, currentPath);
        }

        if (titleMatch || childMatch) {
          hasMatch = true;
          keys.push(...currentPath);
        }
      }
      return hasMatch;
    };
    loop(nodes, []);
    return [...new Set(keys)];
  };

  /** 搜索变更 */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) {
      const matched = getMatchedKeys(displayTreeData, value);
      setExpandedKeys(matched);
      setAutoExpandParent(true);
    } else {
      const firstLevelKeys = displayTreeData.map((n) => n.key);
      setExpandedKeys(firstLevelKeys);
      setAutoExpandParent(false);
    }
  };

  /** 渲染树节点标题（高亮搜索文字 + 类型标签） */
  const renderTreeTitle = (node: OrganizationTreeNode) => {
    const { title, memberCount } = node;
    let titleNode: React.ReactNode = title;

    if (searchValue) {
      const idx = title.toLowerCase().indexOf(searchValue.toLowerCase());
      if (idx > -1) {
        const beforeStr = title.substring(0, idx);
        const matchStr = title.substring(idx, idx + searchValue.length);
        const afterStr = title.substring(idx + searchValue.length);
        titleNode = (
          <span>
            {beforeStr}
            <span style={{ color: '#f50' }}>{matchStr}</span>
            {afterStr}
          </span>
        );
      }
    }

    return (
      <span>
        {titleNode}
        <span style={{ color: '#bbb', fontSize: 11, marginLeft: 4 }}>({memberCount})</span>
      </span>
    );
  };

  /** 将 OrganizationTreeNode 转换为 Ant Design TreeDataNode */
  const convertTreeData = (
    nodes: OrganizationTreeNode[],
  ): TreeProps['treeData'] => {
    return nodes.map((node) => ({
      key: node.key,
      title: renderTreeTitle(node),
      children: node.children ? convertTreeData(node.children) : undefined,
    }));
  };

  // ==================== 操作 ====================

  /** 新建组织 */
  const handleAdd = () => {
    setEditingOrg(null);
    setParentIdForCreate(
      selectedOrg ? selectedOrg.id : undefined,
    );
    setFormVisible(true);
  };

  /** 编辑组织 */
  const handleEdit = () => {
    if (!selectedOrg) return;
    setEditingOrg(selectedOrg);
    setParentIdForCreate(undefined);
    setFormVisible(true);
  };

  /** 删除组织 */
  const handleDelete = () => {
    if (!selectedOrg) return;
    confirm({
      title: '确定要删除该组织吗？',
      icon: <ExclamationCircleOutlined />,
      content: `删除组织「${selectedOrg.name}」后不可恢复，确认继续？`,
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteOrganization(selectedOrg.id);
          message.success('删除成功');
          setSelectedOrg(null);
          setSelectedKeys([]);
          fetchTree();
          fetchStatistics();
        } catch (error) {
          console.error('删除组织失败', error);
        }
      },
    });
  };

  /** 激活组织 */
  const handleActivate = async () => {
    if (!selectedOrg) return;
    try {
      await activateOrganization(selectedOrg.id);
      message.success('已激活');
      fetchDetail(selectedOrg.id);
      fetchTree();
      fetchStatistics();
    } catch (error) {
      console.error('激活组织失败', error);
    }
  };

  /** 停用组织 */
  const handleDeactivate = async () => {
    if (!selectedOrg) return;
    confirm({
      title: '确定要停用该组织吗？',
      icon: <ExclamationCircleOutlined />,
      content: `停用组织「${selectedOrg.name}」后其下属组织和成员可能受到影响。`,
      onOk: async () => {
        try {
          await deactivateOrganization(selectedOrg.id);
          message.success('已停用');
          fetchDetail(selectedOrg.id);
          fetchTree();
          fetchStatistics();
        } catch (error) {
          console.error('停用组织失败', error);
        }
      },
    });
  };

  /** 表单成功回调 */
  const handleFormSuccess = () => {
    setFormVisible(false);
    fetchTree();
    fetchStatistics();
    // 如果在编辑, 刷新详情
    if (editingOrg) {
      fetchDetail(editingOrg.id);
    }
  };

  /** 搜索用户（负责人/助理选择用） */
  const handleSearchUsers = async (keyword: string) => {
    if (!keyword || keyword.length < 1) return;
    setUserSearchLoading(true);
    try {
      const res: any = await getUserList({ realName: keyword, current: 1, pageSize: 20 });
      const data = res?.data?.data || res?.data;
      const items = Array.isArray(data) ? data : data?.list || [];
      setUserOptions(items.map((u: any) => ({ value: u.id, label: `${u.name || u.username} (${u.email || u.phone || ''})` })));
    } catch {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  /** 导出组织 */
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res: any = await exportOrganizations();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `组织架构_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch {
      message.error('导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  /** 导入组织 */
  const handleImport = async (file: File) => {
    try {
      await importOrganizations(file);
      message.success('导入成功');
      setImportVisible(false);
      fetchTree();
      fetchStatistics();
    } catch {
      message.error('导入失败');
    }
    return false; // 阻止 Upload 默认上传
  };

  /** 复制组织 */
  const handleCopy = async () => {
    if (!selectedOrg || !copyName || !copyCode) {
      message.warning('请填写新名称和编码');
      return;
    }
    try {
      await copyOrganization(selectedOrg.id, copyName, copyCode);
      message.success('复制成功');
      setCopyVisible(false);
      setCopyName('');
      setCopyCode('');
      fetchTree();
      fetchStatistics();
    } catch {
      message.error('复制失败');
    }
  };

  /** 更换负责人 */
  const handleSetLeader = async () => {
    if (!selectedOrg || !leaderUserId) {
      message.warning('请选择负责人');
      return;
    }
    try {
      await setOrganizationLeader(selectedOrg.id, leaderUserId);
      message.success('负责人设置成功');
      setLeaderVisible(false);
      setLeaderUserId(undefined);
      setUserOptions([]);
      fetchDetail(selectedOrg.id);
      fetchTree();
    } catch {
      message.error('设置负责人失败');
    }
  };

  /** 移除负责人 */
  const handleRemoveLeader = async () => {
    if (!selectedOrg) return;
    try {
      await removeOrganizationLeader(selectedOrg.id);
      message.success('负责人已移除');
      fetchDetail(selectedOrg.id);
      fetchTree();
    } catch {
      message.error('移除负责人失败');
    }
  };

  /** 编辑助理 */
  const handleSetAssistants = async () => {
    if (!selectedOrg) return;
    try {
      await setOrganizationAssistants(selectedOrg.id, assistantUserIds);
      message.success('助理设置成功');
      setAssistantsVisible(false);
      setAssistantUserIds([]);
      setUserOptions([]);
      fetchDetail(selectedOrg.id);
    } catch {
      message.error('设置助理失败');
    }
  };

  // ==================== 渲染 ====================

  /** 渲染统计卡片 */
  const renderStatistics = () => {
    if (!statistics) return null;
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="组织总数"
              value={statistics.totalOrganizations}
              prefix={<ApartmentOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="成员总数"
              value={statistics.totalMembers}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="公司"
              value={statistics.companies}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="部门"
              value={statistics.departments}
              prefix={<ClusterOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  /** 渲染基本信息 Tab */
  const renderInfoTab = () => {
    if (!selectedOrg) {
      return <Empty description="请在左侧选择一个组织" />;
    }

    return (
      <Spin spinning={detailLoading}>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="组织名称">
            {selectedOrg.name}
          </Descriptions.Item>
          <Descriptions.Item label="组织编码">
            {selectedOrg.code}
          </Descriptions.Item>
          <Descriptions.Item label="组织类型">
            <Tag color={typeColors[selectedOrg.type]}>
              {typeLabels[selectedOrg.type] || '未知'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="层级">
            {selectedOrg.level}
          </Descriptions.Item>
          <Descriptions.Item label="负责人">
            <Space>
              <span>{selectedOrg.leader_name || '-'}</span>
              <Permission permission={PERM_ORGS_UPDATE}>
                <a onClick={() => { setLeaderVisible(true); setLeaderUserId(undefined); setUserOptions([]); }}>
                  更换
                </a>
              </Permission>
              {selectedOrg.leader_id && (
                <Permission permission={PERM_ORGS_UPDATE}>
                  <Popconfirm
                    title="确定移除负责人？"
                    onConfirm={handleRemoveLeader}
                  >
                    <a style={{ color: '#ff4d4f' }}>移除</a>
                  </Popconfirm>
                </Permission>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="助理">
            <Space>
              <span>{(selectedOrg as any).assistants?.map((a: any) => a.name || a.userName).join('、') || '-'}</span>
              <Permission permission={PERM_ORGS_UPDATE}>
                <a onClick={() => { setAssistantsVisible(true); setAssistantUserIds([]); setUserOptions([]); }}>
                  编辑
                </a>
              </Permission>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="成员数">
            {selectedOrg.member_count}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={selectedOrg.status === 'active' ? 'success' : 'default'}>
              {selectedOrg.status === 'active' ? '启用' : '停用'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="排序" span={2}>
            {selectedOrg.sort}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {selectedOrg.created_at}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {selectedOrg.updated_at}
          </Descriptions.Item>
          <Descriptions.Item label="描述" span={2}>
            {selectedOrg.description || '-'}
          </Descriptions.Item>
        </Descriptions>

      </Spin>
    );
  };

  /** 渲染成员列表 Tab */
  const renderMemberTab = () => {
    if (!selectedOrg) {
      return <Empty description="请在左侧选择一个组织" />;
    }
    return (
      <MemberList
        organizationId={selectedOrg.id}
        onMemberChange={() => {
          fetchTree();
          fetchStatistics();
        }}
      />
    );
  };

  /** 渲染子组织 Tab */
  const renderChildrenTab = () => {
    if (!selectedOrg) {
      return <Empty description="请在左侧选择一个组织" />;
    }

    const childColumns: ColumnsType<Organization> = [
      { title: '组织名称', dataIndex: 'name', width: 160 },
      { title: '组织编码', dataIndex: 'code', width: 120 },
      {
        title: '类型',
        dataIndex: 'type',
        width: 80,
        render: (type: number) => (
          <Tag color={typeColors[type]}>{typeLabels[type] || '未知'}</Tag>
        ),
      },
      { title: '负责人', dataIndex: 'leader_name', width: 100, render: (v: string) => v || '-' },
      { title: '成员数', dataIndex: 'member_count', width: 80 },
      {
        title: '状态',
        dataIndex: 'status',
        width: 80,
        render: (status: string) => (
          <Tag color={status === 'active' ? 'success' : 'default'}>
            {status === 'active' ? '启用' : '停用'}
          </Tag>
        ),
      },
    ];

    return (
      <Spin spinning={childOrgsLoading}>
        {childOrgs.length > 0 ? (
          <Table
            rowKey="id"
            columns={childColumns}
            dataSource={childOrgs}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="暂无子组织" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    );
  };

  /** 渲染统计 Tab */
  const renderStatsTab = () => {
    if (!selectedOrg) {
      return <Empty description="请在左侧选择一个组织" />;
    }
    return (
      <Spin spinning={memberStatsLoading}>
        {memberStats ? (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic title="总成员" value={memberStats.totalMembers ?? selectedOrg.member_count ?? 0} prefix={<TeamOutlined />} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic title="子组织数" value={childOrgs.length} prefix={<ApartmentOutlined />} valueStyle={{ color: '#1890ff' }} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic title="新增成员" value={memberStats.newMembers ?? 0} valueStyle={{ color: '#52c41a' }} />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic title="层级深度" value={selectedOrg.level ?? '-'} prefix={<ClusterOutlined />} />
                </Card>
              </Col>
            </Row>
            {memberStats.positionDistribution?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>岗位分布</div>
                <Space wrap>
                  {memberStats.positionDistribution.map((item: any) => (
                    <Tag key={item.positionName}>{item.positionName}: {item.count}</Tag>
                  ))}
                </Space>
              </div>
            )}
            {memberStats.departmentDistribution?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>下属部门分布</div>
                <Space wrap>
                  {memberStats.departmentDistribution.map((item: any) => (
                    <Tag key={item.departmentName} color="blue">{item.departmentName}: {item.count}</Tag>
                  ))}
                </Space>
              </div>
            )}
          </div>
        ) : (
          <Empty description="暂无统计数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Spin>
    );
  };

  /** Tabs 配置 */
  const tabItems = [
    {
      key: 'info',
      label: '基本信息',
      children: renderInfoTab(),
    },
    {
      key: 'members',
      label: '成员管理',
      children: renderMemberTab(),
    },
  ];

  return (
    <div className="organization-management-container">
      {/* 统计卡片 */}
      {renderStatistics()}

      {/* 搜索 + 工具栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        {/* 搜索表单 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', paddingBottom: 12, borderBottom: '1px solid #f0f0f0', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>组织名称</div>
            <Input
              placeholder="请输入组织名称"
              allowClear
              value={searchValue}
              onChange={handleSearchChange}
              style={{ width: 180 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>组织类型</div>
            <Select
              placeholder="请选择组织类型"
              allowClear
              value={filterType ?? undefined}
              onChange={(v) => {
                setFilterType(v ?? null);
                setExpandedKeys(collectAllKeys(treeData));
              }}
              style={{ width: 160 }}
              options={Object.entries(typeLabels).map(([k, label]) => ({ value: Number(k), label }))}
            />
          </div>
          <Button
            onClick={() => {
              setSearchValue('');
              setFilterType(null);
              setExpandedKeys(treeData.map(n => n.key));
            }}
          >
            重置
          </Button>
        </div>
        {/* 工具栏 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>组织架构管理</span>
          <Space>
            <Permission permission={PERM_ORGS_CREATE}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                新建组织
              </Button>
            </Permission>
            <Button icon={<ReloadOutlined />} onClick={() => { fetchTree(); fetchStatistics(); }}>
              刷新
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setImportVisible(true)}>
              导入
            </Button>
            <Button icon={<DownloadOutlined />} loading={exportLoading} onClick={handleExport}>
              导出
            </Button>
          </Space>
        </div>
      </Card>

      {/* 主体：左树右详情 */}
      <Row gutter={16}>
        {/* 左侧树 */}
        <Col span={7}>
          <Card
            title="组织架构"
            size="small"
            styles={{ body: { padding: '12px', minHeight: 500 } }}
            extra={
              <Space size="small">
                <Tooltip title="拖拽排序">
                  <Switch
                    size="small"
                    checked={draggable}
                    onChange={setDraggable}
                    checkedChildren={<DragOutlined />}
                    unCheckedChildren={<DragOutlined />}
                  />
                </Tooltip>
                <Tooltip title="全部展开">
                  <Button type="text" size="small" icon={<ExpandAltOutlined />} onClick={() => setExpandedKeys(collectAllKeys(displayTreeData))} />
                </Tooltip>
                <Tooltip title="全部收起">
                  <Button type="text" size="small" icon={<ShrinkOutlined />} onClick={() => setExpandedKeys([])} />
                </Tooltip>
              </Space>
            }
          >
            <Spin spinning={treeLoading}>
              {displayTreeData.length > 0 ? (
                <Tree
                  treeData={convertTreeData(displayTreeData)}
                  selectedKeys={selectedKeys}
                  expandedKeys={expandedKeys}
                  autoExpandParent={autoExpandParent}
                  onSelect={handleSelect}
                  onExpand={handleExpand}
                  draggable={draggable}
                  onDrop={handleDrop}
                  blockNode
                  showLine={draggable ? false : { showLeafIcon: false }}
                />
              ) : (
                <Empty
                  description="暂无组织数据"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Spin>
          </Card>
        </Col>

        {/* 右侧内容 */}
        <Col span={17}>
          <Card
            title={
              selectedOrg ? (
                <Space size={8}>
                  <Tag color={typeColors[selectedOrg.type]} style={{ margin: 0 }}>
                    {typeLabels[selectedOrg.type] || '未知'}
                  </Tag>
                  <span style={{ fontWeight: 600 }}>{selectedOrg.name}</span>
                  <Tag color={selectedOrg.status === 'active' ? 'success' : 'error'} style={{ margin: 0 }}>
                    {selectedOrg.status === 'active' ? '启用' : '停用'}
                  </Tag>
                </Space>
              ) : '组织详情'
            }
            size="small"
            styles={{ body: { minHeight: 500 } }}
            extra={
              selectedOrg && (
                <Space size={4}>
                  <Permission permission={PERM_ORGS_UPDATE}>
                    <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>编辑</Button>
                  </Permission>
                  <Permission permission={PERM_ORGS_CREATE}>
                    <Button size="small" icon={<PlusOutlined />} onClick={handleAdd}>添加子组织</Button>
                  </Permission>
                  <Permission permission={PERM_ORGS_UPDATE}>
                    {selectedOrg.status === 'active' ? (
                      <Button size="small" icon={<StopOutlined />} onClick={handleDeactivate}>停用</Button>
                    ) : (
                      <Button size="small" icon={<CheckCircleOutlined />} onClick={handleActivate}>激活</Button>
                    )}
                  </Permission>
                  <Permission permission={PERM_ORGS_CREATE}>
                    <Button size="small" icon={<CopyOutlined />} onClick={() => { setCopyName(`${selectedOrg.name}_副本`); setCopyCode(`${selectedOrg.code}_copy`); setCopyVisible(true); }}>复制</Button>
                  </Permission>
                  <Permission permission={PERM_ORGS_DELETE}>
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={handleDelete}>删除</Button>
                  </Permission>
                </Space>
              )
            }
          >
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
            />
          </Card>
        </Col>
      </Row>

      {/* 表单对话框 */}
      <OrganizationForm
        visible={formVisible}
        organization={editingOrg}
        parentId={parentIdForCreate}
        onCancel={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />

      {/* 导入对话框 */}
      <Modal
        title="导入组织"
        open={importVisible}
        onCancel={() => setImportVisible(false)}
        footer={null}
        destroyOnHidden
      >
        <Upload.Dragger
          accept=".xlsx,.xls"
          showUploadList={false}
          beforeUpload={(file) => {
            handleImport(file);
            return false;
          }}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">支持 .xlsx, .xls 格式</p>
        </Upload.Dragger>
      </Modal>

      {/* 复制组织对话框 */}
      <Modal
        title="复制组织"
        open={copyVisible}
        onOk={handleCopy}
        onCancel={() => { setCopyVisible(false); setCopyName(''); setCopyCode(''); }}
        destroyOnHidden
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>新组织名称</div>
          <Input
            value={copyName}
            onChange={(e) => setCopyName(e.target.value)}
            placeholder="请输入新组织名称"
            maxLength={100}
          />
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>新组织编码</div>
          <Input
            value={copyCode}
            onChange={(e) => setCopyCode(e.target.value)}
            placeholder="请输入新组织编码"
            maxLength={50}
          />
        </div>
      </Modal>

      {/* 更换负责人对话框 */}
      <Modal
        title="更换负责人"
        open={leaderVisible}
        onOk={handleSetLeader}
        onCancel={() => { setLeaderVisible(false); setLeaderUserId(undefined); setUserOptions([]); }}
        destroyOnHidden
      >
        <Select
          showSearch
          filterOption={false}
          placeholder="搜索并选择用户"
          value={leaderUserId}
          onChange={setLeaderUserId}
          onSearch={handleSearchUsers}
          loading={userSearchLoading}
          options={userOptions}
          notFoundContent={userSearchLoading ? <Spin size="small" /> : null}
          style={{ width: '100%' }}
        />
      </Modal>

      {/* 编辑助理对话框 */}
      <Modal
        title="编辑助理"
        open={assistantsVisible}
        onOk={handleSetAssistants}
        onCancel={() => { setAssistantsVisible(false); setAssistantUserIds([]); setUserOptions([]); }}
        destroyOnHidden
      >
        <Select
          mode="multiple"
          showSearch
          filterOption={false}
          placeholder="搜索并选择用户（可多选）"
          value={assistantUserIds}
          onChange={setAssistantUserIds}
          onSearch={handleSearchUsers}
          loading={userSearchLoading}
          options={userOptions}
          notFoundContent={userSearchLoading ? <Spin size="small" /> : null}
          style={{ width: '100%' }}
        />
      </Modal>
    </div>
  );
};

export default OrganizationManagement;
